import React, { useState, useEffect } from 'react';
import {
  Mail, RefreshCw, BarChart3, Search, CheckCircle, XCircle,
  Eye, MousePointer, Clock, AlertTriangle, Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import { emailsAPI } from '../services/api';

const TIPO_LABELS = {
  boas_vindas: 'Boas-vindas',
  reset_senha: 'Reset Senha',
  recuperacao_senha: 'Recuperacao',
  acompanhamento: 'Acompanhamento',
  csat: 'CSAT',
  nps: 'NPS',
  generico: 'Generico',
};

const TIPO_COLORS = {
  boas_vindas: '#3498db',
  reset_senha: '#9b59b6',
  recuperacao_senha: '#e67e22',
  acompanhamento: '#20a490',
  csat: '#27ae60',
  nps: '#2980b9',
  generico: '#95a5a6',
};

const STATUS_CONFIG = {
  enviado: { color: '#27ae60', bg: '#e8f8f0', label: 'Enviado', icon: CheckCircle },
  erro: { color: '#e74c3c', bg: '#fdeaea', label: 'Erro', icon: XCircle },
  pendente: { color: '#f39c12', bg: '#fef9e7', label: 'Pendente', icon: Clock },
};

const AdminEmails = () => {
  const [activeTab, setActiveTab] = useState('visao');
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(30);

  // Estatisticas
  const [stats, setStats] = useState(null);

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroDestinatario, setFiltroDestinatario] = useState('');
  const [pagina, setPagina] = useState(0);

  // Detalhe
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    carregarDados();
  }, [activeTab, periodo]);

  useEffect(() => {
    if (activeTab === 'historico') {
      carregarLogs();
    }
  }, [filtroTipo, filtroStatus, pagina]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      if (activeTab === 'visao') {
        const res = await emailsAPI.estatisticas(periodo);
        setStats(res.data);
      } else if (activeTab === 'historico') {
        await carregarLogs();
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const carregarLogs = async () => {
    try {
      const res = await emailsAPI.logs(
        periodo,
        filtroTipo || null,
        filtroStatus || null,
        filtroDestinatario || null,
        pagina * 50,
        50
      );
      setLogs(res.data.items);
      setLogsTotal(res.data.total);
    } catch (error) {
      console.error(error);
    }
  };

  const buscarDestinatario = () => {
    setPagina(0);
    carregarLogs();
  };

  // ========== VISAO GERAL ==========
  const renderVisaoGeral = () => {
    if (!stats) return <div style={styles.emptyState}>Carregando...</div>;

    return (
      <div>
        {/* Cards principais */}
        <div style={styles.cardsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}><Mail size={24} color="#3498db" /></div>
            <div style={styles.statValue}>{stats.total}</div>
            <div style={styles.statLabel}>Emails Enviados</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}><CheckCircle size={24} color="#27ae60" /></div>
            <div style={styles.statValue}>{stats.taxa_entrega}%</div>
            <div style={styles.statLabel}>Taxa de Entrega</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}><Eye size={24} color="#27ae60" /></div>
            <div style={styles.statValue}>{stats.taxa_abertura_real || stats.taxa_abertura}%</div>
            <div style={styles.statLabel}>Abertura Real</div>
            {stats.taxa_abertura_real && stats.taxa_abertura !== stats.taxa_abertura_real && (
              <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 4 }}>
                Total: {stats.taxa_abertura}%
              </div>
            )}
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}><MousePointer size={24} color="#e67e22" /></div>
            <div style={styles.statValue}>{stats.taxa_clique}%</div>
            <div style={styles.statLabel}>Taxa de Clique</div>
          </div>
        </div>

        {/* Resumo numerico */}
        <div style={styles.resumoRow}>
          <span style={{ color: '#27ae60' }}>{stats.enviados} enviados</span>
          <span style={{ color: '#e74c3c' }}>{stats.erros} erros</span>
          <span style={{ color: '#27ae60' }}>{stats.abertos_reais || stats.abertos} abertos</span>
          {stats.abertos_reais && stats.abertos !== stats.abertos_reais && (
            <span style={{ color: '#95a5a6', fontSize: 12 }}>({stats.abertos} total)</span>
          )}
          <span style={{ color: '#e67e22' }}>{stats.clicados} clicados</span>
        </div>

        {/* Por tipo */}
        <div style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>
            <BarChart3 size={18} /> Envios por Tipo
          </h3>
          <div style={styles.tipoGrid}>
            {stats.por_tipo.map((t) => (
              <div key={t.tipo} style={styles.tipoCard}>
                <div style={{ ...styles.tipoBadge, backgroundColor: TIPO_COLORS[t.tipo] || '#95a5a6' }}>
                  {TIPO_LABELS[t.tipo] || t.tipo}
                </div>
                <div style={styles.tipoStats}>
                  <div><strong>{t.total}</strong> enviados</div>
                  <div style={{ color: '#27ae60' }}>{t.abertos_reais || t.abertos} abertos</div>
                  {t.abertos_reais && t.abertos !== t.abertos_reais && (
                    <div style={{ color: '#95a5a6', fontSize: 11 }}>({t.abertos} total)</div>
                  )}
                  <div style={{ color: '#e67e22' }}>{t.clicados} clicados</div>
                </div>
                {t.total > 0 && (
                  <div style={styles.tipoBar}>
                    <div style={{ ...styles.tipoBarFill, width: `${((t.abertos_reais || t.abertos) / t.total) * 100}%`, backgroundColor: '#27ae60' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        {stats.timeline && stats.timeline.length > 0 && (
          <div style={styles.sectionCard}>
            <h3 style={styles.sectionTitle}>
              <Clock size={18} /> Timeline de Envios
            </h3>
            <div style={styles.timelineContainer}>
              {stats.timeline.slice(-14).map((dia) => {
                const maxVal = Math.max(...stats.timeline.map(d => d.total), 1);
                const height = Math.max((dia.total / maxVal) * 100, 4);
                return (
                  <div key={dia.dia} style={styles.timelineBar}>
                    <div style={styles.timelineBarWrapper}>
                      {dia.erros > 0 && (
                        <div style={{ ...styles.timelineBarFill, height: `${(dia.erros / maxVal) * 100}%`, backgroundColor: '#e74c3c', position: 'absolute', bottom: 0 }} />
                      )}
                      <div style={{ ...styles.timelineBarFill, height: `${height}%`, backgroundColor: '#20a490' }} />
                    </div>
                    <div style={styles.timelineLabel}>
                      {dia.dia.slice(5)}
                    </div>
                    <div style={styles.timelineCount}>{dia.total}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========== HISTORICO ==========
  const renderHistorico = () => {
    return (
      <div>
        {/* Filtros */}
        <div style={styles.filtrosRow}>
          <div style={styles.filtroGroup}>
            <Filter size={14} />
            <select
              value={filtroTipo}
              onChange={(e) => { setFiltroTipo(e.target.value); setPagina(0); }}
              style={styles.filtroSelect}
            >
              <option value="">Todos os tipos</option>
              {Object.entries(TIPO_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div style={styles.filtroGroup}>
            <select
              value={filtroStatus}
              onChange={(e) => { setFiltroStatus(e.target.value); setPagina(0); }}
              style={styles.filtroSelect}
            >
              <option value="">Todos os status</option>
              <option value="enviado">Enviado</option>
              <option value="erro">Erro</option>
              <option value="pendente">Pendente</option>
            </select>
          </div>
          <div style={styles.filtroGroup}>
            <input
              type="text"
              placeholder="Buscar destinatario..."
              value={filtroDestinatario}
              onChange={(e) => setFiltroDestinatario(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarDestinatario()}
              style={styles.filtroInput}
            />
            <button onClick={buscarDestinatario} style={styles.filtroBtn}>
              <Search size={14} />
            </button>
          </div>
        </div>

        {/* Tabela */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Destinatario</th>
                <th style={styles.th}>Assunto</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Aberto</th>
                <th style={styles.th}>Cliques</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const sc = STATUS_CONFIG[log.status] || STATUS_CONFIG.pendente;
                return (
                  <tr
                    key={log.id}
                    style={styles.tr}
                    onClick={() => setDetalhe(log)}
                  >
                    <td style={styles.td}>
                      {log.criado_em ? new Date(log.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.tipoBadgeSmall, backgroundColor: TIPO_COLORS[log.tipo] || '#95a5a6' }}>
                        {TIPO_LABELS[log.tipo] || log.tipo}
                      </span>
                    </td>
                    <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.destinatario}
                    </td>
                    <td style={{ ...styles.td, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.assunto || '-'}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, color: sc.color, backgroundColor: sc.bg }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {log.aberto_em ? (
                        <span style={{ color: '#8e44ad' }}>
                          <Eye size={14} /> {log.total_aberturas}x
                        </span>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {log.total_cliques > 0 ? (
                        <span style={{ color: '#e67e22' }}>
                          <MousePointer size={14} /> {log.total_cliques}x
                        </span>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#999', padding: 30 }}>
                    Nenhum email encontrado no periodo
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginacao */}
        <div style={styles.paginacao}>
          <span style={{ color: '#666', fontSize: 13 }}>{logsTotal} emails encontrados</span>
          <div>
            <button
              onClick={() => setPagina(Math.max(0, pagina - 1))}
              disabled={pagina === 0}
              style={styles.paginaBtn}
            >
              Anterior
            </button>
            <span style={{ margin: '0 12px', color: '#666', fontSize: 13 }}>
              Pagina {pagina + 1} de {Math.max(1, Math.ceil(logsTotal / 50))}
            </span>
            <button
              onClick={() => setPagina(pagina + 1)}
              disabled={(pagina + 1) * 50 >= logsTotal}
              style={styles.paginaBtn}
            >
              Proxima
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ========== MODAL DETALHE ==========
  const renderDetalhe = () => {
    if (!detalhe) return null;
    const sc = STATUS_CONFIG[detalhe.status] || STATUS_CONFIG.pendente;

    return (
      <div style={styles.modalOverlay} onClick={() => setDetalhe(null)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h3 style={{ margin: 0 }}>Detalhe do Email</h3>
            <button onClick={() => setDetalhe(null)} style={styles.modalClose}>&times;</button>
          </div>
          <div style={styles.modalBody}>
            <div style={styles.detalheGrid}>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Tipo</div>
                <span style={{ ...styles.tipoBadgeSmall, backgroundColor: TIPO_COLORS[detalhe.tipo] || '#95a5a6' }}>
                  {TIPO_LABELS[detalhe.tipo] || detalhe.tipo}
                </span>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Status</div>
                <span style={{ ...styles.statusBadge, color: sc.color, backgroundColor: sc.bg }}>
                  {sc.label}
                </span>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Destinatario</div>
                <div>{detalhe.destinatario}</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Assunto</div>
                <div>{detalhe.assunto || '-'}</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Criado em</div>
                <div>{detalhe.criado_em ? new Date(detalhe.criado_em).toLocaleString('pt-BR') : '-'}</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Enviado em</div>
                <div>{detalhe.enviado_em ? new Date(detalhe.enviado_em).toLocaleString('pt-BR') : '-'}</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Aberto em</div>
                <div>{detalhe.aberto_em ? new Date(detalhe.aberto_em).toLocaleString('pt-BR') : 'Nao aberto'}</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Aberturas</div>
                <div style={{ color: '#8e44ad', fontWeight: 600 }}>{detalhe.total_aberturas}x</div>
              </div>
              <div style={styles.detalheItem}>
                <div style={styles.detalheLabel}>Cliques</div>
                <div style={{ color: '#e67e22', fontWeight: 600 }}>{detalhe.total_cliques}x</div>
              </div>
              {detalhe.referencia_tipo && (
                <div style={styles.detalheItem}>
                  <div style={styles.detalheLabel}>Referencia</div>
                  <div>{detalhe.referencia_tipo} #{detalhe.referencia_id}</div>
                </div>
              )}
            </div>
            {detalhe.erro_detalhe && (
              <div style={styles.erroBox}>
                <AlertTriangle size={16} color="#e74c3c" />
                <span>{detalhe.erro_detalhe}</span>
              </div>
            )}
            {detalhe.links_clicados && Object.keys(detalhe.links_clicados).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={styles.detalheLabel}>Links clicados</div>
                {Object.entries(detalhe.links_clicados).map(([idx, ts]) => (
                  <div key={idx} style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                    Link #{idx} - {new Date(ts).toLocaleString('pt-BR')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== RENDER ==========
  const tabs = [
    { id: 'visao', label: 'Visao Geral', icon: BarChart3 },
    { id: 'historico', label: 'Historico', icon: Mail },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <Mail size={28} color="#20a490" /> Central de Emails
          </h1>
          <p style={styles.subtitle}>Monitoramento de envios, aberturas e cliques</p>
        </div>
        <div style={styles.headerActions}>
          <select
            value={periodo}
            onChange={(e) => setPeriodo(Number(e.target.value))}
            style={styles.periodoSelect}
          >
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
            <option value={180}>180 dias</option>
          </select>
          <button onClick={carregarDados} style={styles.refreshBtn}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Conteudo */}
      <div style={styles.content}>
        {loading && activeTab === 'visao' ? (
          <div style={styles.emptyState}>
            <RefreshCw size={32} className="spin" color="#20a490" />
            <p>Carregando...</p>
          </div>
        ) : activeTab === 'visao' ? (
          renderVisaoGeral()
        ) : (
          renderHistorico()
        )}
      </div>

      {renderDetalhe()}
    </div>
  );
};

// ========== STYLES ==========
const styles = {
  container: {
    padding: '24px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  subtitle: {
    color: '#666',
    fontSize: 14,
    marginTop: 4,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  periodoSelect: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 13,
    cursor: 'pointer',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    backgroundColor: '#20a490',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  tabsContainer: {
    display: 'flex',
    gap: 4,
    marginBottom: 24,
    borderBottom: '2px solid #eee',
    paddingBottom: 0,
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: -2,
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#20a490',
    borderBottomColor: '#20a490',
    fontWeight: 600,
  },
  content: {
    minHeight: 400,
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    textAlign: 'center',
  },
  statIcon: {
    marginBottom: 8,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
  },
  statLabel: {
    fontSize: 13,
    color: '#666',
    marginTop: 4,
  },
  resumoRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 24,
    fontSize: 13,
    fontWeight: 600,
  },
  sectionCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1a1a2e',
    marginTop: 0,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tipoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  },
  tipoCard: {
    padding: 16,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
  },
  tipoBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    color: 'white',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
  },
  tipoBadgeSmall: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    color: 'white',
    fontSize: 11,
    fontWeight: 600,
  },
  tipoStats: {
    fontSize: 13,
    lineHeight: 1.6,
  },
  tipoBar: {
    height: 4,
    backgroundColor: '#eee',
    borderRadius: 2,
    marginTop: 8,
    overflow: 'hidden',
  },
  tipoBarFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s',
  },
  timelineContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 6,
    height: 140,
    paddingTop: 20,
  },
  timelineBar: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
  },
  timelineBarWrapper: {
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  timelineBarFill: {
    width: '100%',
    borderRadius: '3px 3px 0 0',
    minHeight: 2,
    transition: 'height 0.3s',
  },
  timelineLabel: {
    fontSize: 10,
    color: '#999',
    marginTop: 4,
  },
  timelineCount: {
    fontSize: 10,
    color: '#666',
    fontWeight: 600,
  },
  // Historico
  filtrosRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filtroGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  filtroSelect: {
    padding: '7px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 13,
    cursor: 'pointer',
    backgroundColor: 'white',
  },
  filtroInput: {
    padding: '7px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 13,
    width: 200,
  },
  filtroBtn: {
    padding: '7px 10px',
    borderRadius: 8,
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    borderBottom: '2px solid #eee',
    color: '#666',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #f0f0f0',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
  },
  paginacao: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
    padding: '0 4px',
  },
  paginaBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: 13,
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: '90%',
    maxWidth: 550,
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #eee',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: 24,
    cursor: 'pointer',
    color: '#999',
    padding: 4,
  },
  modalBody: {
    padding: 24,
  },
  detalheGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  detalheItem: {
    fontSize: 14,
  },
  detalheLabel: {
    fontSize: 11,
    color: '#999',
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: 4,
  },
  erroBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    backgroundColor: '#fdeaea',
    borderRadius: 8,
    fontSize: 13,
    color: '#c0392b',
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    color: '#999',
  },
};

export default AdminEmails;
